import { useEffect } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, ArrowRight } from "lucide-react";
import { POSTS } from "@/lib/data";

export default function Article() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const post = POSTS.find((p) => p.slug === slug);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);

  if (!post) {
    return (
      <div className="min-h-[70vh] flex flex-col items-center justify-center text-center px-6">
        <p className="font-serif text-4xl text-[var(--astera-char)]">Article not found</p>
        <Link to="/" className="mt-6 link-underline text-sm uppercase tracking-[0.16em] text-clay">
          Back to home
        </Link>
      </div>
    );
  }

  const more = POSTS.filter((p) => p.slug !== slug).slice(0, 2);

  return (
    <article className="bg-bone" data-testid="article-page">
      {/* Top bar */}
      <header className="sticky top-0 z-50 bg-[rgba(249,248,246,0.8)] backdrop-blur-xl border-b border-[#EAE6E1]">
        <div className="mx-auto max-w-[1000px] px-6 lg:px-8 flex items-center justify-between py-3">
          <Link to="/" data-testid="article-logo" aria-label="ASTERA Women's Health home">
            <img src="/astera-logo.png" alt="ASTERA Women's Health" className="h-12 w-auto object-contain" />
          </Link>
          <Link
            to="/#contact"
            data-testid="article-book-top"
            className="inline-flex items-center rounded-full bg-[var(--astera-char)] px-6 py-3 text-xs uppercase tracking-[0.16em] text-[var(--astera-bone)] transition-colors duration-300 hover:bg-clay"
          >
            Book Appointment
          </Link>
        </div>
      </header>

      {/* Hero */}
      <div className="mx-auto max-w-[1000px] px-6 lg:px-8 pt-14 pb-10">
        <button
          onClick={() => navigate("/#resources")}
          data-testid="article-back"
          className="inline-flex items-center gap-2 text-sm uppercase tracking-[0.16em] text-[var(--astera-char)]/60 hover:text-clay transition-colors"
        >
          <ArrowLeft size={16} /> Journal
        </button>
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
        >
          <p className="mt-8 text-xs uppercase tracking-[0.2em] text-clay">
            {post.tag} · {post.read}
          </p>
          <h1 className="mt-4 font-serif font-light text-4xl md:text-6xl leading-[1.03] tracking-tight text-[var(--astera-char)]">
            {post.title}
          </h1>
          <p className="mt-6 text-lg text-[var(--astera-char)]/65 leading-relaxed max-w-2xl">
            {post.excerpt}
          </p>
        </motion.div>
      </div>

      <div className="mx-auto max-w-[1000px] px-6 lg:px-8">
        <div className="aspect-[16/9] overflow-hidden rounded-[24px]">
          <img src={post.img} alt={post.title} className="h-full w-full object-cover" />
        </div>
      </div>

      {/* Body */}
      <div className="mx-auto max-w-[720px] px-6 lg:px-8 py-16">
        {post.body.map((block, i) =>
          block.type === "h" ? (
            <h2 key={i} className="font-serif text-3xl text-[var(--astera-char)] mt-12 mb-4">
              {block.text}
            </h2>
          ) : (
            <p key={i} className="text-[var(--astera-char)]/75 leading-[1.9] text-[1.05rem] mb-6">
              {block.text}
            </p>
          )
        )}

        {/* CTA */}
        <div className="mt-14 rounded-[24px] bg-char text-[var(--astera-bone)] p-10 text-center">
          <p className="font-serif text-3xl md:text-4xl leading-tight">
            Take your health <span className="italic text-clay">personally.</span>
          </p>
          <p className="mt-3 text-[var(--astera-bone)]/60 max-w-md mx-auto">
            Physician-led care for every stage of womanhood. Begin with a conversation.
          </p>
          <Link
            to="/#contact"
            data-testid="article-book-cta"
            className="group mt-8 inline-flex items-center gap-3 rounded-full bg-clay px-8 py-4 text-sm uppercase tracking-[0.16em] text-[var(--astera-bone)] transition-colors duration-300 hover:bg-[var(--astera-bone)] hover:text-[var(--astera-char)]"
          >
            Book Appointment
            <ArrowRight size={18} className="transition-transform duration-300 group-hover:translate-x-1" />
          </Link>
        </div>
      </div>

      {/* More reading */}
      <div className="mx-auto max-w-[1000px] px-6 lg:px-8 pb-24">
        <p className="mb-8 text-xs uppercase tracking-[0.2em] text-[var(--astera-char)]/45">
          Keep reading
        </p>
        <div className="grid sm:grid-cols-2 gap-8">
          {more.map((p) => (
            <Link
              key={p.slug}
              to={`/journal/${p.slug}`}
              data-testid={`article-more-${p.slug}`}
              className="group block"
            >
              <div className="aspect-square overflow-hidden rounded-[24px]">
                <img
                  src={p.img}
                  alt={p.title}
                  className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105"
                />
              </div>
              <p className="mt-4 text-xs uppercase tracking-[0.14em] text-clay">{p.tag}</p>
              <h3 className="mt-1 font-serif text-2xl text-[var(--astera-char)]">{p.title}</h3>
            </Link>
          ))}
        </div>
      </div>
    </article>
  );
}
